import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { AppText } from '@/components/ui/app-text';
import type { IdeaKind, IdeaStatus } from '@/store/ideas-store';
import { colors, radius, spacing } from '@/theme/tokens';

/** Kind (Pomysł/Błąd) + status (Nowe/W trakcie/Zrobione) pills — mock colors. */
function Pill({ icon, label, bg, fg, solid }: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  bg: string;
  fg: string;
  solid?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: bg,
        borderRadius: radius.full,
        paddingHorizontal: spacing.sm,
        paddingVertical: 3,
      }}
    >
      <Ionicons name={icon} size={13} color={fg} />
      <AppText variant="labelMd" color={fg} style={solid ? { color: fg } : undefined}>
        {label}
      </AppText>
    </View>
  );
}

const STATUS_STYLE: Record<IdeaStatus, { icon: keyof typeof Ionicons.glyphMap; bg: string; fg: string }> = {
  NEW: { icon: 'ellipse-outline', bg: colors.disabledBg, fg: colors.navy },
  IN_PROGRESS: { icon: 'sync-outline', bg: colors.warningBg, fg: colors.warning },
  DONE: { icon: 'checkmark-circle-outline', bg: colors.infoBg, fg: colors.primary },
};

export function IdeaBadges({
  kind,
  status,
  solid = false,
  mine = false,
}: {
  kind: IdeaKind;
  status: IdeaStatus;
  solid?: boolean;
  /** Submitted from this device — the community board has no other way to spot your own entries. */
  mine?: boolean;
}) {
  const { t } = useTranslation();
  const s = STATUS_STYLE[status];
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
      {kind === 'IDEA' ? (
        <Pill
          icon="bulb-outline"
          label={t('ideas.kindIdea')}
          bg={solid ? colors.primary : colors.infoBg}
          fg={solid ? '#FFFFFF' : colors.primary}
        />
      ) : (
        <Pill
          icon="bug-outline"
          label={t('ideas.kindBug')}
          bg={solid ? colors.error : colors.errorBg}
          fg={solid ? '#FFFFFF' : colors.error}
        />
      )}
      <Pill icon={s.icon} label={t(`ideas.status.${status}`)} bg={solid ? s.fg : s.bg} fg={solid ? '#FFFFFF' : s.fg} />
      {mine && (
        <Pill
          icon="person-outline"
          label={t('ideas.mine')}
          bg={solid ? colors.navy : colors.primaryTile}
          fg={solid ? '#FFFFFF' : colors.navy}
        />
      )}
    </View>
  );
}
